/**
 * Common style objects and sx props for the scheduler module
 */

import type { SxProps, Theme } from "@mui/material/styles";
import { alpha } from "@mui/material/styles";
import type { ScheduleItemCalendarView } from "./lib/types";

/**
 * Sidebar collapsed state styles
 */
export const sidebarCollapsedStyles: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  height: "100%",
  borderRadius: 0,
  bgcolor: "background.paper",
  alignItems: "center",
  py: 2,
};

/**
 * Sidebar expanded state styles
 */
export const sidebarExpandedStyles: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  height: "100%",
  borderRadius: 0,
  bgcolor: "background.paper",
};

/**
 * Sidebar header styles (primary color) - Theme-aware
 */
export const sidebarHeaderPrimaryStyles: SxProps<Theme> = (theme) => ({
  p: 2.5,
  borderBottom: 2,
  borderColor: "divider",
  bgcolor: theme.palette.mode === "dark" ? "grey.800" : "primary.main",
  position: "relative",
  boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
});

/**
 * Sidebar header styles (secondary color) - Theme-aware
 */
export const sidebarHeaderSecondaryStyles: SxProps<Theme> = (theme) => ({
  p: 2.5,
  borderBottom: 2,
  borderColor: "divider",
  bgcolor: theme.palette.mode === "dark" ? "grey.800" : "secondary.main",
  position: "relative",
  boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
});

/**
 * Sidebar toggle button styles
 */
export const sidebarToggleButtonStyles: SxProps<Theme> = {
  bgcolor: "action.hover",
  "&:hover": { bgcolor: "primary.light", color: "primary.main" },
};

/**
 * Scrollbar styles for sidebars
 */
export const sidebarScrollbarStyles: SxProps<Theme> = {
  flex: 1,
  overflowY: "auto",
  overflowX: "hidden",
  p: 2,
  "&::-webkit-scrollbar": {
    width: "6px",
  },
  "&::-webkit-scrollbar-track": {
    bgcolor: "background.default",
    borderRadius: 1,
  },
  "&::-webkit-scrollbar-thumb": {
    bgcolor: "action.disabled",
    borderRadius: 1,
    "&:hover": {
      bgcolor: "action.active",
    },
  },
};

/**
 * Card base styles
 */
export const cardBaseStyles: SxProps<Theme> = {
  borderRadius: 2,
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
};

/**
 * Card hover styles
 */
export const cardHoverStyles: SxProps<Theme> = {
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: 2,
    borderColor: "primary.main",
  },
};

/**
 * Drag and drop card styles
 */
export const getDragCardStyles = (
  isDragging: boolean,
  dragOver: boolean = false,
  isDisabled: boolean = false,
): SxProps<Theme> => ({
  ...cardBaseStyles,
  opacity: isDisabled ? 0.7 : isDragging ? 0.5 : dragOver ? 1 : 1,
  transform: isDragging
    ? "rotate(2deg) scale(0.95)"
    : dragOver
      ? "scale(1.02)"
      : "scale(1)",
  cursor: isDisabled ? "not-allowed" : isDragging ? "grabbing" : "grab",
  "&:active": { cursor: isDisabled ? "not-allowed" : "grabbing" },
  boxShadow: isDragging
    ? "0 12px 32px rgba(0, 0, 0, 0.15)"
    : dragOver
      ? "0 8px 24px rgba(25, 118, 210, 0.3)"
      : isDisabled
        ? "0 2px 8px rgba(211, 47, 47, 0.2)"
        : "0 2px 8px rgba(0, 0, 0, 0.1)",
});

/**
 * Priority border left styles
 */
export const getPriorityBorderStyles = (
  priority: "error" | "warning" | "default" | "info" | "inherit",
): SxProps<Theme> => ({
  borderLeft: "3px solid",
  borderLeftColor:
    priority === "error"
      ? "error.main"
      : priority === "warning"
        ? "warning.main"
        : priority === "info"
          ? "info.main"
          : "divider",
});

/**
 * Status-based border left styles
 * Uses WorkOrderStatus color from cache if available, falls back to priority color
 * @param woStatusId - Work Order Status ID
 * @param priority - Fallback priority color
 */
export const getStatusBorderStyles = (
  woStatusId: number | undefined,
  priority: "error" | "warning" | "default" | "info" | "inherit" = "inherit",
): SxProps<Theme> => {
  // Try to get status color from cache
  if (woStatusId && woStatusId > 0) {
    try {
      const username = localStorage.getItem("username") || "currentUserIdentifier";
      const cacheKey = `WorkOrderStatuses_${username}`;
      const storedData = localStorage.getItem(cacheKey);
      if (storedData) {
        const parsedData = JSON.parse(storedData);
        const statuses = parsedData.data || parsedData || [];
        const status = statuses.find((s: any) => s.identity === woStatusId);
        if (status) {
          const backColor = status.backColor || status.BackColor;
          if (backColor) {
            return {
              borderLeft: "3px solid",
              borderLeftColor: backColor,
            };
          }
        }
      }
    } catch (error) {
      console.error("Error reading WorkOrderStatuses from cache:", error);
    }
  }

  // Fallback to priority-based color
  return {
    borderLeft: "3px solid",
    borderLeftColor:
      priority === "error"
        ? "error.main"
        : priority === "warning"
          ? "warning.main"
          : priority === "info"
            ? "info.main"
            : "divider",
  };
};

/**
 * Border styles for completed/archived schedule item views.
 * Active view uses existing WO status border styling instead.
 */
export const getScheduleItemViewBorderStyles = (
  theme: Theme,
  scheduleItemView: ScheduleItemCalendarView,
): SxProps<Theme> => {
  if (scheduleItemView === "completed") {
    return {
      border: `2px solid ${alpha(theme.palette.success.main, 0.55)}`,
      borderLeft: `4px solid ${theme.palette.success.main}`,
      bgcolor: alpha(theme.palette.success.main, 0.08),
      boxShadow: `0 0 0 1px ${alpha(theme.palette.success.main, 0.12)}`,
      "&:hover": {
        borderColor: alpha(theme.palette.success.main, 0.75),
        boxShadow: `0 2px 8px ${alpha(theme.palette.success.main, 0.2)}`,
      },
    };
  }

  if (scheduleItemView === "archived") {
    return {
      border: `2px solid ${alpha(theme.palette.error.main, 0.55)}`,
      borderLeft: `4px solid ${theme.palette.error.main}`,
      bgcolor: alpha(theme.palette.error.main, 0.08),
      boxShadow: `0 0 0 1px ${alpha(theme.palette.error.main, 0.12)}`,
      "&:hover": {
        borderColor: alpha(theme.palette.error.main, 0.75),
        boxShadow: `0 2px 8px ${alpha(theme.palette.error.main, 0.2)}`,
      },
    };
  }

  return {};
};

const scheduleItemViewFilterColors = (
  theme: Theme,
  view: ScheduleItemCalendarView,
) => {
  switch (view) {
    case "completed":
      return theme.palette.success;
    case "archived":
      return theme.palette.error;
    default:
      return theme.palette.primary;
  }
};

/** Container for the schedule item view pill filter */
export const scheduleItemViewFilterContainerStyles: SxProps<Theme> = {
  display: "inline-flex",
  alignItems: "center",
  flexShrink: 0,
};

export const scheduleItemViewFilterLabelStyles: SxProps<Theme> = {
  fontSize: "0.75rem",
  fontWeight: 600,
  color: "text.secondary",
  whiteSpace: "nowrap",
  letterSpacing: "0.01em",
};

/** Compact dropdowns so the filter row stays on one line at md/lg */
export const capacityBoardFilterControlStyles: SxProps<Theme> = {
  minWidth: 118,
  maxWidth: 168,
  flex: "1 1 118px",
  "& .MuiInputLabel-root": {
    fontSize: "0.875rem",
  },
  "& .MuiInputLabel-shrink": {
    fontSize: "0.875rem",
    transform: "translate(14px, -9px) scale(1)",
  },
  "& .MuiOutlinedInput-root": {
    fontSize: "0.75rem",
    height: 36,
  },
  "& .MuiSelect-select": {
    py: 0.75,
    pr: "28px !important",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
};

export const capacityBoardToolbarRowStyles: SxProps<Theme> = {
  display: "flex",
  gap: 1,
  alignItems: "center",
  justifyContent: "space-between",
  flexWrap: "nowrap",
  width: "100%",
  minWidth: 0,
};

export const capacityBoardFilterGroupStyles: SxProps<Theme> = {
  display: "flex",
  gap: 1,
  alignItems: "center",
  flexWrap: "nowrap",
  flex: "1 1 auto",
  minWidth: 0,
};

export const capacityBoardWeekNavGroupStyles: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  gap: 0.75,
  flexShrink: 0,
  minHeight: 36,
};

/** Pill group background matching filter toolbar controls */
export const scheduleItemViewFilterGroupStyles = (theme: Theme): SxProps<Theme> => ({
  display: "inline-flex",
  alignItems: "center",
  p: 0.25,
  gap: 0.25,
  borderRadius: 1.25,
  bgcolor:
    theme.palette.mode === "dark"
      ? alpha(theme.palette.common.white, 0.04)
      : alpha(theme.palette.common.black, 0.03),
  border: `1px solid ${theme.palette.divider}`,
  boxShadow:
    theme.palette.mode === "dark"
      ? "none"
      : "0 1px 2px rgba(0, 0, 0, 0.04)",
});

/** Individual pill option within the schedule item view filter */
export const scheduleItemViewFilterOptionStyles = (
  theme: Theme,
  view: ScheduleItemCalendarView,
  isSelected: boolean,
): SxProps<Theme> => {
  const palette = scheduleItemViewFilterColors(theme, view);

  return {
    display: "inline-flex",
    alignItems: "center",
    gap: 0.375,
    px: 0.875,
    py: 0.375,
    borderRadius: 1,
    border: "none",
    cursor: "pointer",
    fontSize: "0.7rem",
    fontWeight: isSelected ? 600 : 500,
    fontFamily: "inherit",
    lineHeight: 1.3,
    textTransform: "none",
    whiteSpace: "nowrap",
    color: isSelected ? palette.main : theme.palette.text.secondary,
    bgcolor: isSelected ? alpha(palette.main, 0.12) : "transparent",
    boxShadow: isSelected ? `0 1px 2px ${alpha(palette.main, 0.18)}` : "none",
    transition: "background-color 0.2s ease, color 0.2s ease, box-shadow 0.2s ease",
    "& .MuiSvgIcon-root": {
      fontSize: "0.85rem",
      opacity: isSelected ? 1 : 0.75,
    },
    "&:hover": {
      bgcolor: isSelected
        ? alpha(palette.main, 0.18)
        : alpha(theme.palette.action.hover, 0.08),
      color: isSelected ? palette.main : theme.palette.text.primary,
    },
  };
};

export const weekDayRangeFilterOptionStyles = (
  theme: Theme,
  isSelected: boolean,
): SxProps<Theme> => ({
  display: "inline-flex",
  alignItems: "center",
  px: 0.875,
  py: 0.375,
  borderRadius: 1,
  border: "none",
  cursor: "pointer",
  fontSize: "0.7rem",
  fontWeight: isSelected ? 600 : 500,
  fontFamily: "inherit",
  lineHeight: 1.3,
  textTransform: "none",
  whiteSpace: "nowrap",
  color: isSelected ? theme.palette.primary.main : theme.palette.text.secondary,
  bgcolor: isSelected ? alpha(theme.palette.primary.main, 0.12) : "transparent",
  boxShadow: isSelected
    ? `0 1px 2px ${alpha(theme.palette.primary.main, 0.18)}`
    : "none",
  transition: "background-color 0.2s ease, color 0.2s ease, box-shadow 0.2s ease",
  "&:hover": {
    bgcolor: isSelected
      ? alpha(theme.palette.primary.main, 0.18)
      : alpha(theme.palette.action.hover, 0.08),
    color: isSelected ? theme.palette.primary.main : theme.palette.text.primary,
  },
});

/**
 * Capacity status color styles
 */
export const getCapacityStatusColor = (
  availableHours: number,
  maxCapacity: number,
  isFull: boolean,
): "error" | "warning" | "success" => {
  if (availableHours < 0) return "error";
  if (isFull || availableHours === 0) return "error";
  if (availableHours >= maxCapacity * 0.5) return "success";
  return "warning";
};

/**
 * Capacity indicator circle styles
 */
export const getCapacityIndicatorStyles = (
  availableHours: number,
  maxCapacity: number,
  isFull: boolean,
): SxProps<Theme> => ({
  width: 14,
  height: 14,
  borderRadius: "50%",
  bgcolor:
    availableHours < 0
      ? "error.main"
      : isFull || availableHours === 0
        ? "error.main"
        : availableHours >= maxCapacity * 0.5
          ? "success.main"
          : "warning.main",
  border: 2,
  borderColor: "background.paper",
  boxShadow: "0 2px 4px rgba(0,0,0,0.15)",
});

/**
 * Drop zone styles
 */
export const dropZoneStyles: SxProps<Theme> = {
  minHeight: 16,
  borderRadius: 0.5,
  bgcolor: "transparent",
  transition: "all 0.15s ease",
};

/**
 * Work order group header styles (collapsed multi-segment groups)
 */
export const workOrderGroupHeaderStyles = (
  isExpanded: boolean,
  isDragged: boolean,
): SxProps<Theme> => ({
  p: 0,
  position: "relative",
  overflow: "hidden",
  cursor: isDragged ? "grabbing" : "grab",
  bgcolor: isDragged ? "primary.light" : "background.default",
  border: isDragged ? 2 : 1,
  borderStyle: "solid",
  borderColor: isDragged ? "primary.main" : "divider",
  borderRadius: 1,
  transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
  transform: isDragged ? "scale(1.02)" : "scale(1)",
  "&:hover": {
    boxShadow: 2,
    transform: "translateY(-1px)",
    borderColor: "primary.main",
  },
  ...(isExpanded && {
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
    borderBottom: 0,
  }),
});

/**
 * Work order group title row — heading band for WO number (light/dark safe)
 */
export const workOrderGroupTitleRowStyles: SxProps<Theme> = (theme) => ({
  display: "flex",
  alignItems: "center",
  gap: 0.5,
  minWidth: 0,
  px: 0.75,
  py: 0.5,
  borderBottom: 1,
  borderColor:
    theme.palette.mode === "dark"
      ? alpha(theme.palette.primary.main, 0.28)
      : alpha(theme.palette.primary.main, 0.18),
  bgcolor:
    theme.palette.mode === "dark"
      ? alpha(theme.palette.primary.main, 0.14)
      : alpha(theme.palette.primary.main, 0.07),
  backgroundImage:
    theme.palette.mode === "dark"
      ? `linear-gradient(90deg, ${alpha(theme.palette.primary.light, 0.14)} 0%, transparent 72%)`
      : `linear-gradient(90deg, ${alpha(theme.palette.primary.main, 0.12)} 0%, transparent 72%)`,
});

/**
 * Work order group title text
 */
export const workOrderGroupTitleTextStyles = (
  compact = false,
): SxProps<Theme> => (theme) => ({
  display: "block",
  fontWeight: 800,
  fontSize: compact ? "0.7rem" : "0.74rem",
  letterSpacing: "0.03em",
  color:
    theme.palette.mode === "dark"
      ? theme.palette.primary.light
      : theme.palette.primary.dark,
  minWidth: 0,
  lineHeight: 1.25,
  fontVariantNumeric: "tabular-nums",
});

/**
 * Work order group meta row — segments + hours summary
 */
export const workOrderGroupMetaRowStyles: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  gap: 0.5,
  px: 0.75,
  py: 0.5,
  minWidth: 0,
  flexWrap: "nowrap",
  whiteSpace: "nowrap",
  cursor: "pointer",
};

export const workOrderGroupExpandToggleStyles: SxProps<Theme> = (theme) => ({
  p: 0.25,
  flexShrink: 0,
  color: "primary.main",
  width: 24,
  height: 24,
  minWidth: 24,
  minHeight: 24,
  bgcolor: alpha(theme.palette.primary.main, 0.1),
  border: 1,
  borderColor: alpha(theme.palette.primary.main, 0.28),
  "&:hover": {
    bgcolor: alpha(theme.palette.primary.main, 0.18),
    borderColor: theme.palette.primary.main,
  },
});

export const workOrderGroupExpandIndicatorStyles: SxProps<Theme> = {
  ml: "auto",
  display: "flex",
  alignItems: "center",
  flexShrink: 0,
  color: "primary.main",
  opacity: 0.95,
};

export const workOrderGroupDetailRowStyles: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  gap: 0.15,
  px: 0.75,
  pb: 0.5,
  minWidth: 0,
  cursor: "pointer",
};

export const workOrderGroupDetailTextStyles: SxProps<Theme> = {
  fontSize: "0.6rem",
  fontWeight: 500,
  color: "text.secondary",
  lineHeight: 1.3,
  minWidth: 0,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

export const workOrderGroupDetailLabelStyles: SxProps<Theme> = {
  fontWeight: 600,
  color: "text.disabled",
  letterSpacing: "0.01em",
  mr: 0.5,
};

/**
 * Segment item action header (WO number hidden — used inside expanded groups)
 */
export const scheduleItemActionHeaderStyles: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  gap: 0.5,
  minWidth: 0,
  px: 0.75,
  py: 0.375,
  borderBottom: 1,
  borderColor: "divider",
  bgcolor: (theme) => alpha(theme.palette.primary.main, 0.035),
};

/**
 * Work order group children container styles
 */
export const workOrderGroupChildrenStyles: SxProps<Theme> = {
  border: 1,
  borderTop: 0,
  borderStyle: "solid",
  borderColor: "divider",
  borderBottomLeftRadius: 4,
  borderBottomRightRadius: 4,
  bgcolor: "background.default",
  display: "flex",
  flexDirection: "column",
  gap: 0.75,
  p: 0.75,
  pt: 0.5,
};

/**
 * Drop zone after item styles
 */
export const dropZoneAfterItemStyles: SxProps<Theme> = {
  minHeight: 12,
  borderRadius: 0.5,
  bgcolor: "transparent",
  transition: "all 0.15s ease",
};

/**
 * Icon button small styles
 */
export const iconButtonSmallStyles: SxProps<Theme> = {
  p: 0.25,
  opacity: 0.6,
  transition: "all 0.2s",
  "&:hover": {
      opacity: 1,
      bgcolor: "error.light",
    color: "white",
  },
};
export const iconButtonSmallStylesOpenWorkOrder: SxProps<Theme> = {
  p: 0.25,
  opacity: 0.6,
  transition: "all 0.2s",
  color: "#2371E7",
  "&:hover": {
      opacity: 1,
      bgcolor: "primary.light",
    color: "white",
  },
};

export const iconButtonSmallStylesMarkComplete: SxProps<Theme> = {
  p: 0.25,
  opacity: 0.6,
  transition: "all 0.2s",
  color: "success.main",
  "&:hover": {
    opacity: 1,
    bgcolor: "success.light",
    color: "white",
  },
};
/**
 * Work order card styles
 */
export const workOrderCardStyles: SxProps<Theme> = {
  p: 1,
  bgcolor: "background.paper",
  border: "1px solid",
  borderColor: "divider",
  borderRadius: 1.5,
  boxShadow: "0 1px 4px rgba(0, 0, 0, 0.08)",
};

/**
 * Work order header hover styles
 */
export const workOrderHeaderHoverStyles: SxProps<Theme> = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: 0.5,
  cursor: "pointer",
  "&:hover": {
    bgcolor: "action.hover",
    borderRadius: 1,
  },
  p: 0.25,
};

/**
 * Priority chip dot styles
 */
export const priorityChipDotStyles: SxProps<Theme> = {
  width: 10,
  height: 10,
  minWidth: 10,
  minHeight: 10,
  borderRadius: "50%",
  p: 0,
  "& .MuiChip-label": {
    display: "none",
  },
};

/**
 * Segment connector line styles (vertical)
 */
export const segmentConnectorVerticalStyles: SxProps<Theme> = {
  position: "absolute",
  left: 12,
  top: 0,
  bottom: 0,
  width: 2,
  bgcolor: "divider",
  opacity: 0.3,
};

/**
 * Segment connector line styles (horizontal)
 */
export const segmentConnectorHorizontalStyles: SxProps<Theme> = {
  position: "absolute",
  left: -20,
  top: 20,
  width: 16,
  height: 2,
  bgcolor: "divider",
  opacity: 0.3,
};

/**
 * Technician card disabled styles
 */
export const getTechnicianCardDisabledStyles = (
  isDisabled: boolean,
  dragOver: boolean,
): SxProps<Theme> => ({
  border: dragOver ? 3 : isDisabled ? 2 : 1,
  borderStyle: "solid",
  borderColor: isDisabled
    ? "error.main"
    : dragOver
      ? "primary.main"
      : "divider",
  bgcolor: isDisabled
    ? "error.light"
    : dragOver
      ? "primary.light"
      : "background.paper",
  opacity: isDisabled ? 0.7 : 1,
  cursor: isDisabled ? "not-allowed" : "default",
  "&::before":
    dragOver && !isDisabled
      ? {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: "4px",
          bgcolor: "primary.main",
          zIndex: 1,
        }
      : isDisabled
        ? {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: "4px",
            bgcolor: "error.main",
            zIndex: 1,
          }
        : {},
});

/**
 * Capacity board header styles
 */
export const capacityBoardHeaderStyles: SxProps<Theme> = {
  p: "5px",
  borderBottom: 2,
  borderColor: "divider",
  display: "flex",
  flexDirection: "column",
  bgcolor: "background.paper",
  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
  width: "100%",
  minWidth: "1200px", // Ensure minimum width for horizontal scroll alignment
};

/**
 * Capacity board title bar styles - Theme-aware
 */
export const capacityBoardTitleBarStyles: SxProps<Theme> = (theme) => ({
  bgcolor: theme.palette.mode === "dark" ? "grey.800" : "primary.main",
  color: "white",
  px: 2,
  py: 1,
  borderRadius: 1,
  boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  width: "100%",
  cursor: "pointer",
  "&:hover": {
    bgcolor: theme.palette.mode === "dark" ? "grey.700" : "primary.dark",
  },
});

/**
 * Week navigation button styles
 */
export const weekNavigationButtonStyles: SxProps<Theme> = {
  width: 30,
  height: 30,
  bgcolor: "background.paper",
  border: 1,
  borderColor: "divider",
  borderRadius: 2,
  color: "text.primary",
  "&:hover": {
    bgcolor: "action.hover",
    borderColor: "primary.main",
  },
};

/**
 * Reset filters button styles
 */
export const resetFiltersButtonStyles: SxProps<Theme> = {
  width: 32,
  height: 32,
  flexShrink: 0,
  bgcolor: "background.paper",
  border: 1,
  borderColor: "divider",
  borderRadius: 1.5,
  color: "text.primary",
  "&:hover": {
    bgcolor: "action.hover",
    borderColor: "primary.main",
    color: "primary.main",
  },
};

/**
 * Day header paper styles
 */
export const dayHeaderPaperStyles: SxProps<Theme> = {
  p: 1.25,
  textAlign: "center",
  bgcolor: (theme) =>
    theme.palette.mode === "dark" ? "#6a6868" : theme.palette.primary.main,
  color:   "white",
  border: 1, // Match technician card border thickness for alignment
  borderColor: "divider",
  borderRadius: 1.5,
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  position: "relative",
  overflow: "hidden",
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: 3,
    borderColor: "primary.main",
    bgcolor: (theme) =>
      theme.palette.mode === "dark" ? "#6a6868" : theme.palette.primary.main,
  },
};

/**
 * Sticky header row styles
 */
export const stickyHeaderRowStyles: SxProps<Theme> = {
  display: "grid",
  gridTemplateColumns: "120px repeat(7, 1fr)",
  gap: 1.5,
  mb: 2,
  position: "sticky",
  top: 0,
  zIndex: 20,
  pt: 1,
  pb: 1.5,
  px: 0,
  bgcolor: "background.paper",
  borderBottom: 2,
  borderColor: "divider",
  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
  borderRadius: "0 0 8px 8px",
  backdropFilter: "blur(8px)",
  WebkitBackdropFilter: "blur(8px)",
  minWidth: "1200px", // Ensure minimum width for horizontal scroll alignment
};

/**
 * Technician row grid styles
 */
export const technicianRowGridStyles: SxProps<Theme> = {
  display: "grid",
  gridTemplateColumns: "repeat(7, minmax(0, 1fr))", // 7 equal day columns
  gap: 1.5,
  bgcolor: "background.paper",
  border: 1,
  borderColor: "divider",
  borderRadius: 2,
  p: 2, // Padding inside the grid container
  alignItems: "start", // Align items to start to match header
  minWidth: "1200px", // Ensure minimum width for horizontal scroll alignment
};

/**
 * Technician avatar paper styles
 */
export const technicianAvatarPaperStyles: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: 1,
  py: 2,
  px: 1.5,
  bgcolor: "transparent",
  height: "fit-content",
  minHeight: 180,
  position: "sticky",
  left: 0,
  zIndex: 15,
};
